<template>

  <div class="page page-route-builder">

    <!-- Notifications -->
    <div class="notification-area">
      <data-editor-feedback
          resource="route"
          :autoCloseAfterMillis="5000" />
    </div>

    <!-------------------------->
    <!-- Breadcrumb & Actions -->
    <!-------------------------->
    <v-row class="mt-4 mb-3 d-flex align-center">
      <v-col cols="12" xs="12" sm="7" md="6" lg="6" class="d-flex align-center justify-start page-breadcrumb-new">
        <!-- (breadcrumb) -->
        <v-icon small class="mr-2">mdi-directions-fork</v-icon>
        <router-link :to="{ name: 'route-list-created' }">Routes</router-link>
        <div class="separator">/</div>
        <div class="active-item" v-if="routeToEdit"><span class="label-prefix">Building:</span> {{ routeToEdit.name }}</div>
      </v-col>
      <v-col cols="12" xs="12" sm="5" md="6" lg="6" class="pr-5 d-flex align-center justify-center justify-sm-end" v-if="!isDataLoading && !isDispatchProcessing">
        <!-- (cancel, close, save, generate) -->
        <a class="mr-5" @click="close" v-if="isDirty && !isMapDataLoading">Cancel</a>
        <a class="mr-5" @click="close" v-else-if="!isMapDataLoading">Close</a>
        <v-btn color="primary" @click="generateRoute" :disabled="isMapDataLoading" v-if="regenerateRouteRequired && scheduledStartTime">Generate Route</v-btn>
        <v-btn color="primary" @click="save" v-else-if="isDirty">Save Changes</v-btn>
      </v-col>
    </v-row>

    <!-- Page Content -->
    <v-container fluid class="pa-0">

      <!-- (data loading or dispatch processing) -->
      <template v-if="isDataLoading || isDispatchProcessing">
        <v-row>
          <v-col class="d-flex align-center justify-center" style="height:120px">
            <v-progress-circular indeterminate :size="50" color="primary" />
          </v-col>
        </v-row>
      </template>

      <!---------------------->
      <!-- Route Builder UI -->
      <!---------------------->
      <template v-else>
        <!---------------------->
        <!-- Test Mode Banner -->
        <!---------------------->
        <template v-if="isTestMode">
          <v-row align="center" justify="center" class="px-3" style="text-align:center;">
            <v-col cols="12" class="py-2 mt-2 mb-3 d-flex flex-column align-center justify-center test-mode-banner-container">
              <div class="title white--text d-flex align-center">
                <span>TEST ROUTE</span>
              </div>
            </v-col>
          </v-row>
        </template>

        <!------------------------------->
        <!-- Dispatched Editing Banner -->
        <!------------------------------->
        <template v-if="isRouteDispatchedEditing">
          <v-row align="center" justify="center" class="px-3" style="text-align:center;">
            <v-col cols="12" class="my-3 pb-4 d-flex flex-column align-center justify-center dispatched-editing-banner-container">
              <div class="title white--text d-flex align-center">
                <v-icon color="amber lighten-3" class="pr-3">mdi-clock-edit-outline</v-icon>
                <span>You are editing a dispatched Route.</span>
              </div>
              <div class="mt-2">Delivery is blocked until this Route is again dispatched.</div>
            </v-col>
          </v-row>
        </template>

        <!------------------------->
        <!-- Order notifications -->
        <!------------------------->
        <template v-if="isNotificationDataLoading">
          <v-row>
            <v-col class="d-flex align-center justify-center" style="">
              <v-progress-circular indeterminate :size="20" color="amber darken-2" />
              <div style="color:#727272;" class="pl-3">Checking order statuses...</div>
            </v-col>
          </v-row>
        </template>
        <template v-else-if="unDeliverableOrders.length > 0 && isRouteEditable">
          <transition name="fade" appear>
            <v-container fluid class="pt-0 mb-4 mt-6">
              <v-row align="center" justify="center">
                <v-col cols="12" xs="12" sm="11" md="9" lg="7" class="pb-4 notification-container">
                  <v-row>
                    <v-col cols="12" class="pb-3 d-flex align-center justify-center">
                      <v-icon color="amber darken-2" class="mr-3">mdi-alert</v-icon>
                      <div class="header-text">Provider statuses have changed for orders:</div>
                      <v-btn plain icon color="white" class="ml-2 pt-1" @click="showNotificationList = !showNotificationList">
                        <v-icon style="font-size:28px;" v-if="!showNotificationList">mdi-chevron-down</v-icon>
                        <v-icon style="font-size:28px;" v-else>mdi-chevron-up</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                  <transition name="slide-down" appear>
                    <div v-if="showNotificationList" style="margin-top:10px;">
                      <v-row class="mt-3" v-for="(statusInfo, index) in unDeliverableOrders" :key="`order-check-${index}`">
                        <v-col cols="12" xs="12" sm="4" md="4" lg="4" class="pt-3 d-flex align-center justify-center">
                          <span style="color:#727272" class="pr-2">order:</span>
                          <router-link class="order-number" :to="{ name: 'order-info-overview', params: { orderId: statusInfo.order_id } }">
                            {{ statusInfo.order_number }}
                          </router-link>
                        </v-col>
                        <v-col cols="12" xs="12" sm="3" md="3" lg="3" class="pt-2 d-flex align-center justify-center">
                        <span style="color:#727272" class="pr-2">status:</span> {{ statusInfo.thirdparty_status }}
                        </v-col>
                        <v-col cols="12" xs="12" sm="5" md="5" lg="5" class="pt-2 d-flex align-center justify-center">
                          <v-btn outlined height="26" color="amber darken-2" @click="removeOrderById(statusInfo.order_id)">
                            <span style="font-size:0.84rem;">Remove from Route</span>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </div>
                  </transition>
                </v-col>
              </v-row>
            </v-container>
          </transition>
        </template>

        <!------------------------------------->
        <!-- Appointment Confirmation Status -->
        <!------------------------------------->
        <template v-if="isRouteEditable && requiringAppointmentsOrderCount > 0">
          <v-row align="center" justify="center" class="px-3 mt-2" style="text-align:center;">
            <v-col
                cols="12" xs="12" sm="12" md="8" lg="7"
                :class="(appointmentsAreSatisfied) ? 'appointment-status-banner-container-satisfied' : 'appointment-status-banner-container'"
                class="my-3 pb-4 d-flex align-center justify-center">
              <v-icon color="red" style="font-size:20px;margin-right:13px;" v-if="!appointmentsAreSatisfied">mdi-calendar-clock-outline</v-icon>
              <span :class="(appointmentsAreSatisfied) ? 'appointments-status-satisfied' : 'appointments-status'">
                {{ confirmedAppointmentsOrderCount }}/{{ requiringAppointmentsOrderCount }} Appointments Confirmed
              </span>
              <v-icon color="green" class="ml-2" v-if="appointmentsAreSatisfied">mdi-check-circle</v-icon>
            </v-col>
          </v-row>
        </template>

        <!------------------------------------------------>
        <!-- Route generation or dispatch error message -->
        <!------------------------------------------------>
        <template v-if="routeGenerationError || routeDispatchError">
          <v-row align="center" justify="center" class="my-6">
            <v-col cols="12" xs="12" sm="12" md="8" lg="7" class="d-flex align-center justify-center error-container">
              <v-icon color="red darken-2" style="font-size:28px;" class="mr-2">mdi-alert-circle-outline</v-icon>
              <span class="error-message-text" v-if="routeGenerationError">{{ routeGenerationError }}</span>
              <span class="error-message-text" v-if="routeDispatchError">{{ routeDispatchError }}</span>
              <v-btn class="ml-6 mr-3" @click="clearError">Close</v-btn>
            </v-col>
          </v-row>
        </template>

        <!---------------------------->
        <!-- Route builder elements -->
        <!---------------------------->
        <v-row>
          <v-col cols="12" xs="12" sm="12" md="6" lg="5">
            <!-- Route Item Editor (name, start time, vehicle, driver) -->
            <route-item-editor
                :route="routeToEdit"
                :isRouteEditable="isRouteEditable"
                v-on:dirty="setDirty"
                v-on:startTimeSet="updateStartTime"
                v-on:dispatchRoute="dispatch"
                v-on:updated="reloadAndRefreshRoute" />
          </v-col>
          <!-- Route Map Display -->
          <v-col cols="12" xs="12" sm="12" md="6" lg="7" align="center">
            <v-progress-circular indeterminate :size="65" style="margin-top:85px;" color="primary" v-if="isMapDataLoading" />
            <map-viewer
                v-else-if="hq"
                height="570px"
                :settings="mapSettings"
                :markers="[hq]"
                :deliverySteps="deliverySteps"
                :routeLayer="routeLayer" />
          </v-col>
        </v-row>

        <!-- Route Order Manager (add/remove orders, appt confirm, dynamic arrival times) -->
        <v-row class="mt-10 mb-8">
          <v-col cols="12" class="page-content-controls" align="center">
            <route-order-manager
                ref="routeOrderManager"
                :route="routeToEdit"
                :isRouteEditable="isRouteEditable"
                :orders="orders"
                :isManualMode="isManualMode"
                :permitAddOrders="permitAddOrders"
                :permitRemoveOrders="permitRemoveOrders"
                v-on:dirty="setDirty"
                v-on:switchMode="setRouteMode"
                v-on:generateRoute="generateRoute"
                v-on:orderMoved="moveOrder"
                v-on:orderAdd="addOrder"
                v-on:orderRemove="removeOrder"
                v-on:deleteRoute="deleteRoute" />
          </v-col>
        </v-row>
      </template>

    </v-container>
  </div>

</template>

<script>
/* eslint-disable no-unneeded-ternary */
/* global mapboxgl */
import DataEditorFeedback from '@/components/app/DataEditorFeedback'
import RouteItemEditor from '@/components/routes/RouteItemEditor'
import RouteOrderManager from '@/components/routes/RouteOrderManager'
import MapViewer from '@/components/map/MapViewer'

export default {
  name: 'RouteBuilderPage',

  components: {
    DataEditorFeedback,
    RouteItemEditor,
    RouteOrderManager,
    MapViewer
  },

  props: {
    routeId: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      isManualMode: true,
      defaultManual: true,
      isDirty: false,
      dirtyProps: [],
      isDataLoading: false,
      isDataSaving: false, // NOTE - The UI is not using this status yet
      // ------ order notifications
      isNotificationDataLoading: false,
      notificationDataError: null,
      showNotificationList: false,
      // ------ map data
      isMapDataLoading: false,
      deliverySteps: [],
      mapBounds: [],
      // ------ route generation
      routeGenerationError: null,
      regenerateRouteRequired: false,
      // ------ dispatch handling
      isDispatchProcessing: false,
      routeDispatchError: null,
      // ------ loaded data
      hq: null,
      requiresAppointment: [],
      providerStatusChecks: [],
      routeActive: null,
      routeToEdit: null,
      routeLayer: null
    }
  },

  computed: {
    // The origin route that brought the user to this page
    origin () {
      return this.$store.getters['sh/itemEditingOrigin']('route')
    },
    // If route is test or live
    isTestMode () {
      return this.routeToEdit.is_test
    },
    // The parking lot (start/end of route)
    vehicleLot () {
      return this.routeToEdit.vehicle_lot
    },
    // The orders in the Route
    orders () {
      return this.routeToEdit.orders
    },
    // Any orders that have provider statuses indicating no longer deliverable
    unDeliverableOrders () {
      return this.providerStatusChecks.filter(it =>
        it.order_status === 'BEING_SCHEDULED' && (
          it.thirdparty_status === 'rejected' ||
          it.thirdparty_status === 'cancelled' ||
          it.thirdparty_status === 'not-found'
        ))
    },
    // Order count for orders that require appointments
    requiringAppointmentsOrderCount () {
      return this.requiresAppointment.length
    },
    // Confirmed count per orders that require appointments
    confirmedAppointmentsOrderCount () {
      return this.requiresAppointment.filter(order => order.appointment_confirmed === true).length
    },
    appointmentsAreSatisfied () {
      return this.requiringAppointmentsOrderCount === this.confirmedAppointmentsOrderCount
    },
    // Map settings
    mapSettings () {
      const coords = this.mapBounds
      const bounds = coords.reduce((bounds, coord) => {
        return bounds.extend(coord)
      }, new mapboxgl.LngLatBounds(coords[0], coords[0]))

      return {
        minZoom: 5,
        maxZoom: 18,
        zoom: 9,
        bounds,
        fitBoundsOptions: { padding: { top: 40, bottom: 40, left: 40, right: 40 } }
      }
    },
    scheduledStartTime () {
      return this.routeToEdit.scheduled_start_time
    },
    isRouteDispatchedEditing () {
      return this.routeToEdit.route_status === 'DISPATCHED_EDITING'
    },
    isRouteEditable () {
      return this.routeToEdit.route_status === 'CREATED' || this.routeToEdit.route_status === 'DISPATCHED_EDITING'
    },
    // Show the generate button when a route has already been processed (unless it has been dispatched)
    showGenerateRouteButton () {
      return (this.routeToEdit.estimated_return_time && this.isRouteEditable)
        ? true
        : false
    },
    // Allow add orders until route is dispatched
    permitAddOrders () {
      return this.isRouteEditable
    },
    // Allow remove orders until route is dispatched
    permitRemoveOrders () {
      return this.isRouteEditable
    }
  },

  async beforeMount () {
    this.isDataLoading = true

    // Load route to edit
    await this.loadRouteToBuild()

    // Set mode
    this.isManualMode = (this.routeToEdit.route_info && this.routeToEdit.route_info.manual === true) ? true : false

    // Check provider order statuses
    this.checkProviderOrderStatus()

    // Load existing route layer
    this.loadSavedRouteLayer()

    this.isDataLoading = false
  },

  beforeDestroy () {
    // Clear editing registry for route
    this.$store.dispatch('sh/clearItemEditing', 'route')
  },

  methods: {
    // Manage dirty state
    setDirty (propName, order) {
      let isDiff = false
      if (propName.endsWith('_license_issue_date') || propName.endsWith('_license_expiration') || propName.endsWith('_amended_date')) {
        // isDiff = (!this.item[propName] && !this.itemToEdit[propName])
        //   ? false
        //   : !moment(this.item[propName]).tz(this.userSettings.timezone).isSame(moment(this.itemToEdit[propName]).tz(this.userSettings.timezone))
      } else if (propName === 'appointment_confirmed') {
        // NOTE: This property is on the order
        if (order) {
          const activeOrder = this.routeActive.orders.find(it => it.id === order.id)
          const orderToEdit = this.routeToEdit.orders.find(it => it.id === order.id)
          isDiff = activeOrder[propName] !== orderToEdit[propName]
        } else {
          isDiff = true
        }
      } else {
        isDiff = this.routeActive[propName] !== this.routeToEdit[propName]
      }

      if (isDiff) {
        if (!this.$root.utils.includes(this.dirtyProps, propName)) this.dirtyProps.push(propName)
        this.isDirty = true
      } else {
        const dirtyPropIndex = this.dirtyProps.findIndex(prop => prop === propName)
        if (dirtyPropIndex !== -1) this.dirtyProps.splice(dirtyPropIndex, 1)
        if (this.dirtyProps.length === 0) this.isDirty = false
      }
    },

    // Close/cancel
    close () {
      if (this.origin && this.origin.name !== this.$route.name) {
        this.$root.goTo(this.origin.name, this.origin.params) // return to origin route
      } else {
        this.$root.goTo('route-list-created') // otherwise, return to list page
      }
    },

    // Clear existing errors
    clearError () {
      this.routeGenerationError = null
      this.routeDispatchError = null
    },

    // Handle changes to the start time
    updateStartTime () {
      if (!this.isManualMode) {
        this.generateRoute() // generate route and estimations
      } else {
        this.regenerateRouteRequired = true // flag route re-generation required
      }
    },

    // Handle changes to the "mode" (manual vs automatic)
    async setRouteMode (mode) {
      try {
        if (mode === 'manual') {
          // Clear estimated times already calculated
          await this.clearEstimatedTimes()

          // Set to manual mode
          this.isManualMode = true
          this.routeToEdit.route_info.manual = true

          // Flag route re-generation required
          this.regenerateRouteRequired = true
        } else {
          // Set to automatic mode
          this.isManualMode = false
          this.routeToEdit.route_info.manual = false

          // Re-generate route calculation
          if (this.routeToEdit.scheduled_start_time) await this.generateRoute()
        }

        // Save order and route updates
        await this.save()
      } catch (err) {
        console.log('Error switching route modes =>', err)
      }
    },

    // Clear estimated times on route and orders
    async clearEstimatedTimes () {
      this.isDataLoading = true

      try {
        // Clear estimatations for route and orders
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `route/${this.routeId}/clear-estimations`,
          to_model: false
        })

        // Reload route
        await this.loadRouteToBuild()

        // Clear route layer data
        this.routeLayer = null
      } catch (err) {
        console.log('Error clearing estimates =>', err)
      }

      this.isDataLoading = false
    },

    // Reload route and force refresh of UI
    async reloadAndRefreshRoute () {
      this.isDataLoading = true
      await this.loadRouteToBuild()
      this.isDataLoading = false
    },

    // Save changes
    async save () {
      this.isDataSaving = true

      // Save route changes
      await this.$store.dispatch('sh/updateData', { data: this.routeToEdit, resource: 'route', noFeedback: true })

      // Save appointment confirmation changes to associated orders
      if (this.orders && this.$root.utils.includes(this.dirtyProps, 'appointment_confirmed')) {
        for (const order of this.orders) {
          await this.$store.dispatch('sh/dataApi', {
            method: 'post',
            uri: `order/${order.id}`,
            opts: {
              bodyData: { appointment_confirmed: order.appointment_confirmed }
            }
          })
        }
      }

      // Reset dirty flags
      this.dirtyProps = []
      this.isDirty = false
      this.isDataSaving = false
    },

    // Dispatch route
    async dispatch () {
      this.isDispatchProcessing = true

      try {
        await this.save() // save any changes

        // Dispatch route
        if (this.isRouteDispatchedEditing) {
          await this.$store.dispatch('sh/dataApi', {
            method: 'post',
            uri: `/route/${this.routeId}/re-dispatch`
          })
        } else {
          await this.$store.dispatch('sh/dataApi', {
            method: 'post',
            uri: `/route/${this.routeId}/dispatch`
          })
        }

        // Reload route and orders
        await this.loadRouteToBuild()
      } catch (err) {
        this.routeDispatchError = err.message || err.text || err
      } finally {
        this.isDispatchProcessing = false
      }
    },

    async moveOrder () {
      if (this.isManualMode) {
        this.regenerateRouteRequired = true // flag route re-generation required
      }
    },

    async addOrder (order) {
      if (!order) return false
      this.isDataLoading = true

      try {
        // Add order to route
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `route/${this.routeId}/orders`,
          opts: {
            bodyData: { order_ids: [order.id] }
          }
        })

        // Reload route
        await this.loadRouteToBuild()

        // Check provider order statuses
        this.checkProviderOrderStatus()

        if (this.isManualMode) {
          await this.clearEstimatedTimes() // if manual: clear estimations
          this.regenerateRouteRequired = true // flag route re-generation required
        } else if (this.routeToEdit.scheduled_start_time) {
          await this.generateRoute() // if auto: re-generate route
        }

        // Save order and route updates
        await this.save()
      } catch (err) {
        console.log('Error adding order to route =>', err)
      }

      this.isDataLoading = false
    },

    async removeOrderById (orderId) {
      this.removeOrder({ id: orderId })
    },

    async removeOrder (order) {
      if (!order) return false
      this.isDataLoading = true

      try {
        // Remove order from route
        await this.$store.dispatch('sh/dataApi', {
          method: 'delete',
          uri: `route/${this.routeId}/orders`,
          opts: {
            bodyData: { order_ids: [order.id] }
          }
        })

        // Reload route
        await this.loadRouteToBuild()

        // Check provider order statuses
        this.checkProviderOrderStatus()

        if (this.isManualMode) {
          await this.clearEstimatedTimes() // if manual: clear estimations
          this.regenerateRouteRequired = true // flag route re-generation required
        } else if (this.routeToEdit.scheduled_start_time) {
          await this.generateRoute() // if auto: re-generate route
        }

        // Save order and route updates
        await this.save()
      } catch (err) {
        console.log('Error removing order from route =>', err)
      }

      this.isDataLoading = false
    },

    async loadRouteToBuild () {
      // Fetch route and set to edit
      this.routeToEdit = await this.$store.dispatch('sh/dataApi', {
        method: 'get',
        uri: `route/${this.routeId}`,
        opts: {
          params: {
            with: 'orders,orders.shipper,orders.receiver,vehicle,vehicle_lot,driver'
          }
        }
      })

      // Build "hq" data (vehicle lot)
      const vehicleLot = this.routeToEdit.vehicle_lot || {}
      const lon = this.$root.utils.get(vehicleLot, 'coords.longitude', null)
      const lat = this.$root.utils.get(vehicleLot, 'coords.latitude', null)
      this.hq = {
        coordinates: (lon && lat) ? [lon, lat] : null,
        properties: {
          name: vehicleLot.name,
          alias: vehicleLot.alias,
          type: 'vehicle-lot',
          route: this.routeToEdit
        }
      }
      if (this.hq.coordinates) this.mapBounds.push(this.hq.coordinates)

      // Build delivery steps for map
      this.buildDeliverySteps()

      // Save copy for diff
      this.routeActive = this.$root.utils.cloneDeep(this.routeToEdit)

      // Flag route re-generation required if necessary
      if (this.routeToEdit.scheduled_start_time && !this.routeToEdit.estimated_return_time) this.regenerateRouteRequired = true

      // Check for orders requiring appointments (before route can be dispatched)
      if (this.isRouteEditable) this.checkRequiredAppointments()
    },

    // Load saved route layer for map
    async loadSavedRouteLayer () {
      // Only load if route estimations have been generated (otherwise the existing layer is outdated)
      if (!this.routeToEdit.estimated_return_time) return false

      const accessUrl = await this.$root.fetchRouteAsset(this.routeId, 'map_layer_route')
      try {
        const res = await fetch(accessUrl)
        const layerData = await res.json()
        this.routeLayer = layerData
      } catch (err) {}
    },

    // Check the provider order status for all orders
    async checkProviderOrderStatus () {
      if (!this.orders || this.orders.length === 0) return false
      this.isNotificationDataLoading = true

      try {
        const orderIds = this.orders.map(it => it.id)

        // Check provider status
        const statusChecks = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: 'order-provider-status',
          opts: {
            bodyData: { order_ids: orderIds }
          },
          to_model: false
        })

        this.providerStatusChecks = statusChecks.data
      } catch (err) {
        this.notificationDataError = err.message || err.text || err
      } finally {
        this.isNotificationDataLoading = false
      }
    },

    // Identity orders that require appointments with receiver locations
    checkRequiredAppointments () {
      if (this.orders && this.orders.length > 0) {
        this.requiresAppointment = this.orders.filter(order => (order.order_status !== 'RESCHEDULED' && order.receiver && order.receiver.requires_appointment === true))
      }
    },

    // Build delivery steps and bounds (for map)
    buildDeliverySteps () {
      const deliverySteps = []

      if (this.routeToEdit?.route_info?.steps && !this.regenerateRouteRequired) {
        // Iterate all generated steps and build data for map display
        for (const [index, step] of this.routeToEdit.route_info.steps.entries()) {
          const orders = step.order_ids.map(id => this.routeToEdit.orders.filter(order => order.id === id)[0])

          // Access location info from one of the orders
          if (orders && orders.length > 0 && orders[0]) {
            const order = orders[0] // we only need one order to determine location info, since globbed

            // Determine location and parse coordinates
            const location = (step.type === 'pickup') ? order.shipper : order.receiver
            const locationType = (step.type === 'pickup') ? 'pickup-location' : 'dropoff-location'
            if (location && location.coords && location.coords.longitude && location.coords.latitude) {
              const coords = [
                this.$root.utils.truncateDecimals(location.coords.longitude, 5),
                this.$root.utils.truncateDecimals(location.coords.latitude, 5)
              ]

              // Build map marker and add to bounds
              this.mapBounds.push(coords)
              deliverySteps.push({
                coordinates: coords,
                properties: {
                  step_index: index + 1,
                  type: locationType,
                  name: location.name,
                  orders
                }
              })
            } // end-if (location && location.coords && location.coords.longitude && location.coords.latitude)
          } // end-if (orders && orders.length > 0)
        } // end-for (this.routeInfo.steps)
      } else {
        // Build markers before a route has been generated
        const pickupSteps = {}
        const dropoffSteps = {}

        for (const order of this.routeToEdit.orders) {
          const shipper = order.shipper
          if (!pickupSteps[shipper.id]) {
            pickupSteps[shipper.id] = {
              coordinates: [
                this.$root.utils.truncateDecimals(shipper.coords.longitude, 5),
                this.$root.utils.truncateDecimals(shipper.coords.latitude, 5)
              ],
              properties: {
                type: 'pickup-location',
                name: shipper.name,
                orders: [order]
              }
            }
          } else {
            pickupSteps[shipper.id].properties.orders.push(order)
          }

          const receiver = order.receiver
          if (!dropoffSteps[receiver.id]) {
            dropoffSteps[receiver.id] = {
              coordinates: [
                this.$root.utils.truncateDecimals(receiver.coords.longitude, 5),
                this.$root.utils.truncateDecimals(receiver.coords.latitude, 5)
              ],
              properties: {
                type: 'dropoff-location',
                name: receiver.name,
                orders: [order]
              }
            }
          } else {
            dropoffSteps[receiver.id].properties.orders.push(order)
          }
        }

        // Add pickup steps to delivery steps
        for (const step of Object.values(pickupSteps)) {
          this.mapBounds.push(step.coordinates)
          deliverySteps.push(step)
        }

        // Add dropoff steps to delivery steps
        for (const step of Object.values(dropoffSteps)) {
          this.mapBounds.push(step.coordinates)
          deliverySteps.push(step)
        }
      }

      this.deliverySteps = deliverySteps
    },

    // Generate route layer data for map display
    async generateRoute () {
      this.isMapDataLoading = true

      try {
        // Save other route changes first, if necessary
        if (this.isDirty) await this.save()

        const manualData = (this.isManualMode)
          ? this.$refs.routeOrderManager.sortedLocations
          : null

        // Generate route and update route and order estimations
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `route/${this.routeId}/generate`,
          opts: {
            bodyData: {
              scheduled_start_time: this.routeToEdit.scheduled_start_time,
              is_manual_mode: this.isManualMode,
              locations: manualData
            }
          },
          to_model: false
        })

        // Set dirty flags
        this.regenerateRouteRequired = false
        this.isDirty = false

        // Reload route
        await this.reloadAndRefreshRoute()
        this.loadSavedRouteLayer()
      } catch (err) {
        console.error('Error generating route:', err)
        if (this.$root.utils.isEmpty(err)) this.routeGenerationError = 'The route could not be generated. Possibly too many unique locations to handle.'
        else this.routeGenerationError = err.message || err.text || err
      } finally {
        this.isMapDataLoading = false
      }
    },

    async uploadRouteLayer (layerData) {
      if (!layerData) return false

      const assetKey = 'map_layer_route'
      let uploadInfo = null
      try {
        // Handle route-related upload
        uploadInfo = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `route/${this.routeId}/asset/uploadUrl`,
          opts: {
            params: { asset_key: assetKey }
          },
          to_model: false
        })

        return (uploadInfo)
          ? fetch(uploadInfo.url, { method: 'PUT', body: JSON.stringify(layerData) })
          : false
      } catch (err) {
        console.log(`Error uploading route layer asset: ${assetKey} =>`, err)
        throw err
      }
    },

    async deleteRoute () {
      try {
        // Delete route
        await this.$store.dispatch('sh/dataApi', {
          method: 'delete',
          uri: `route/${this.routeId}`,
          to_model: false
        })

        this.$root.goTo('route-list-created') // return to list page on success
      } catch (err) {
        console.error('There was a problem deleting the route =>', err)
      }
    }
  }
}
</script>

<style scoped>
  /* Appointment Handling */
  .appointment-status-banner-container {
    font-size: 1.04rem;
    /* color: #c9c9c9; */
    border: 1px solid #F94751;
    border-radius: 4px;
  }

  .appointment-status-banner-container-satisfied {
    font-size: 1.04rem;
    border: 1px solid #4CAF4F;
    border-radius: 4px;
  }

  .page-route-builder .appointments-status {
    font-size: 17px;
    color: #F94751;
  }

  .page-route-builder .appointments-status-satisfied {
    font-size: 17px;
    color: #FFFFFF;
  }

  /* Notifications */
  .page-route-builder .notification-container {
    border: 1px solid #FF9D29;
    border-radius: 4px;
  }

  .page-route-builder .notification-container .header-text {
    font-size: 1.1rem;
    font-weight: 500;
  }

  .page-route-builder .notification-container .order-number {
    font-size: 1.1rem;
    color: #FF9D29;
    font-weight: 500;
  }

  .page-route-builder .notification-container a.order-number {
    text-decoration: none;
  }

  .page-route-builder .notification-container a.order-number:hover {
    text-decoration: underline;
  }

  /* Test Mode banner */
  .test-mode-banner-container {
    font-size: 1.04rem;
    color: #c9c9c9;
    /* border: 2px solid #E65000; */
    background-color: #E65000;
    border-radius: 4px;
  }

  /* Dispatched Editing banner */
  .dispatched-editing-banner-container {
    font-size: 1.04rem;
    color: #c9c9c9;
    border: 2px solid #FF9D29;
    border-radius: 4px;
  }

  /* Error messages */
  .page-route-builder .error-container {
    border: 1px solid #D32F2F;
    border-radius: 4px;
  }

  .page-route-builder .error-message-text {
    font-size: 1.08em;
    color: #D32F2F;
    font-weight: 400;
  }

  /* Route order manager */
  .page-route-builder .route-order-manager {
    max-width: 1280px;
  }

  /* Slide down transition */
  .slide-down-enter-active {
    transition: all 0.3s ease-out;
  }

  .slide-down-leave-active {
    transition: all 0.3s ease-in;
  }

  .slide-down-enter-from,
  .slide-down-leave-to {
    transform: translateY(-20px);
    opacity: 0;
  }
</style>
